import { useSyncExternalStore } from 'react'

/**
 * State of the in-house resource center (#25353), outside React so the module-level hand-off in
 * `shared/util/userflowVisibility.ts` (called by HubSpot chat and the GitBook embed) can drive it.
 *
 * - `active`: a `ResourceCenter` is mounted with `resourceCenterNext` on. While true, the Userflow
 *   resource center stays hidden and the hand-off moves this one instead.
 * - `open`: the panel is showing.
 * - `cornerYielded`: another widget (HubSpot chat, GitBook docs) holds the bottom-right corner, so the
 *   launcher is hidden.
 */
export interface ResourceCenterState {
  active: boolean
  open: boolean
  cornerYielded: boolean
}

const INITIAL: ResourceCenterState = { active: false, open: false, cornerYielded: false }

let state: ResourceCenterState = INITIAL
const listeners = new Set<() => void>()

// Hand-off bookkeeping, kept out of `state` because nothing renders from it.
// - `handingOff`: true only while `handOffFromResourceCenter` runs an entry. A widget that yields the
//   corner during that call (they all yield synchronously, from the window event) was opened by the panel.
// - `returnToPanel`: the widget holding the corner was opened from the panel, so giving the corner
//   back may re-open it. A widget opened anywhere else (a help link in the app) leaves it false.
let handingOff = false
let returnToPanel = false

function update(next: Partial<ResourceCenterState>) {
  const merged = { ...state, ...next }
  if (merged.active === state.active && merged.open === state.open && merged.cornerYielded === state.cornerYielded) {
    return
  }
  state = merged
  listeners.forEach((listener) => listener())
}

export function getResourceCenterState(): ResourceCenterState {
  return state
}

export function subscribeResourceCenter(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isResourceCenterActive(): boolean {
  return state.active
}

/** Called by the mounted `ResourceCenter`. Going inactive also closes the panel and frees the corner. */
export function setResourceCenterActive(active: boolean): void {
  if (!active) returnToPanel = false
  update(active ? { active } : INITIAL)
}

/**
 * Opens the panel, and takes the corner back. Returns false (and does nothing) while no
 * `ResourceCenter` is active, so a caller tracks only a real open.
 */
export function openResourceCenter(): boolean {
  if (!state.active) return false
  update({ open: true, cornerYielded: false })
  return true
}

export function closeResourceCenter(): void {
  update({ open: false })
}

/**
 * Closes the panel and runs `open`, which opens another widget (GitBook docs, HubSpot chat). A widget
 * that takes the corner during `open` is remembered as opened from the panel, so closing it can
 * return the user to the panel (`releaseResourceCenterCorner`).
 */
export function handOffFromResourceCenter<T>(open: () => T): T {
  closeResourceCenter()
  handingOff = true
  try {
    return open()
  } finally {
    handingOff = false
  }
}

/** Hides the launcher (and closes the panel) while another widget holds the corner, or gives it back. */
export function setResourceCenterCornerYielded(yielded: boolean): void {
  if (yielded) {
    // A second widget taking the corner from the first (GitBook's Contact Support opens chat) keeps
    // the first one's answer unless the panel opened the second.
    returnToPanel = handingOff || (state.cornerYielded && returnToPanel)
  } else {
    returnToPanel = false
  }
  update(yielded ? { cornerYielded: true, open: false } : { cornerYielded: false })
}

/**
 * Gives the corner back once the other widget closes. With `reopen`, re-opens the panel too, but only
 * when the panel opened that widget: closing docs opened from a help link elsewhere must not pop the
 * panel open. Returns true when the panel opened.
 */
export function releaseResourceCenterCorner({ reopen }: { reopen: boolean }): boolean {
  const back = reopen && returnToPanel && state.cornerYielded
  setResourceCenterCornerYielded(false)
  return back ? openResourceCenter() : false
}

export function useResourceCenterState(): ResourceCenterState {
  return useSyncExternalStore(subscribeResourceCenter, getResourceCenterState, getResourceCenterState)
}

/** Test-only: back to the initial state. */
export function resetResourceCenterForTests(): void {
  state = INITIAL
  handingOff = false
  returnToPanel = false
  listeners.clear()
}
